import { useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Columns3, FilterX,
} from 'lucide-react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';

function SortIndicator({ direction }) {
  if (direction === 'asc') return <ArrowUp size={13} />;
  if (direction === 'desc') return <ArrowDown size={13} />;
  return <ChevronsUpDown size={13} />;
}

function numberRangeFilter(row, columnId, value) {
  const [minimum, maximum] = value || [];
  const rawValue = row.getValue(columnId);
  const numericValue = rawValue == null || rawValue === '' ? null : Number(rawValue);
  if (numericValue == null || Number.isNaN(numericValue)) return !minimum && !maximum;
  return (!minimum || numericValue >= Number(minimum)) && (!maximum || numericValue <= Number(maximum));
}
numberRangeFilter.autoRemove = value => !value?.some(Boolean);

function dateRangeFilter(row, columnId, value) {
  const [from, to] = value || [];
  const rawValue = row.getValue(columnId);
  if (!rawValue) return !from && !to;
  const dateValue = new Date(rawValue).getTime();
  if (Number.isNaN(dateValue)) return false;
  return (!from || dateValue >= new Date(from).getTime()) && (!to || dateValue <= new Date(`${to}T23:59:59.999`).getTime());
}
dateRangeFilter.autoRemove = value => !value?.some(Boolean);

function ColumnFilter({ column }) {
  const variant = column.columnDef.meta?.filterVariant;
  const filterValue = column.getFilterValue();
  const label = column.columnDef.meta?.label || column.id;

  if (variant === 'select') {
    const configuredOptions = column.columnDef.meta?.filterOptions;
    const options = configuredOptions || [...column.getFacetedUniqueValues().keys()]
      .filter(value => value != null && value !== '')
      .sort((a, b) => String(a).localeCompare(String(b)));
    return (
      <select
        className="data-table-column-filter data-table-select-filter"
        aria-label={`Filter ${label}`}
        value={filterValue || ''}
        onChange={event => column.setFilterValue(event.target.value || undefined)}
      >
        <option value="">All</option>
        {options.map(option => <option key={String(option)} value={option}>{option}</option>)}
      </select>
    );
  }

  if (variant === 'numberRange' || variant === 'dateRange') {
    const [minimum, maximum] = Array.isArray(filterValue) ? filterValue : ['', ''];
    const inputType = variant === 'dateRange' ? 'date' : 'number';
    return (
      <div className="data-table-range-filter">
        <input
          className="data-table-column-filter"
          type={inputType}
          min={variant === 'numberRange' ? 0 : undefined}
          placeholder="Min"
          aria-label={`Minimum ${label}`}
          value={minimum || ''}
          onChange={event => column.setFilterValue([event.target.value, maximum])}
        />
        <span>-</span>
        <input
          className="data-table-column-filter"
          type={inputType}
          min={variant === 'numberRange' ? 0 : undefined}
          placeholder="Max"
          aria-label={`Maximum ${label}`}
          value={maximum || ''}
          onChange={event => column.setFilterValue([minimum, event.target.value])}
        />
      </div>
    );
  }

  return (
    <input
      className="data-table-column-filter"
      type="search"
      aria-label={`Search ${label}`}
      placeholder="Search..."
      value={filterValue || ''}
      onChange={event => column.setFilterValue(event.target.value || undefined)}
    />
  );
}

export function DataTable({ columns, data, emptyMessage = 'No results.', initialPageSize = 20 }) {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: initialPageSize });

  const table = useReactTable({
    data,
    columns,
    filterFns: { numberRange: numberRangeFilter, dateRange: dateRangeFilter },
    state: { sorting, columnFilters, columnVisibility, pagination },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const sourceRows = table.getPreFilteredRowModel().rows.length;
  const totalRows = table.getFilteredRowModel().rows.length;
  const startRow = totalRows ? pagination.pageIndex * pagination.pageSize + 1 : 0;
  const endRow = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalRows);

  return (
    <div className="data-table-shell">
      <div className="data-table-toolbar">
        <span>{columnFilters.length ? `${totalRows} of ${sourceRows}` : totalRows} row{totalRows === 1 ? '' : 's'}</span>
        {columnFilters.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => table.resetColumnFilters()}>
            <FilterX size={14} />Clear filters
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm"><Columns3 size={14} />Columns</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table.getAllLeafColumns().filter(column => column.getCanHide()).map(column => (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                onCheckedChange={value => column.toggleVisibility(Boolean(value))}
              >
                {column.columnDef.meta?.label || column.id}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="table-wrap data-table-wrap">
        <Table className="users-table">
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div className="data-table-header-content">
                        {header.column.getCanSort() ? (
                          <button
                            type="button"
                            className="data-table-sort-button"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <SortIndicator direction={header.column.getIsSorted()} />
                          </button>
                        ) : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanFilter() && header.column.columnDef.meta?.filterVariant && (
                          <ColumnFilter column={header.column} />
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? table.getRowModel().rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            )) : (
              <TableRow><TableCell className="data-table-empty" colSpan={table.getVisibleLeafColumns().length}>{emptyMessage}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="data-table-pagination">
        <label>
          Rows per page
          <select
            className="form-input"
            value={pagination.pageSize}
            onChange={event => table.setPageSize(Number(event.target.value))}
          >
            {[10, 20, 50].map(size => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <span>{startRow}–{endRow} of {totalRows}</span>
        <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
          <ChevronLeft size={14} />Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
          Next<ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}
